<template>
  <card-dialog
    :value="value"
    @input="val => $emit('input', val)"
    :title="$t('label.edit_place')"
    :width="500"
  >
    <v-form v-model="valid" ref="form">
      <form-field :label="$t('field.name')">
        <text-field
          v-model="form.name"
          :rules="[noDuplicates, required($t('field.name'))]"
          data-testid="name"
        />
      </form-field>
      <form-field :label="$t('field.assignees')" v-if="featureIsActive('s_type_place_assignees')">
        <v-select
          v-model="form.assignee_ids"
          :items="organizationUsers"
          item-value="id"
          item-text="name"
          multiple
          chips
          deletable-chips
          :label="$t('field.select_assignees')"
          data-testid="assignees"
        />
      </form-field>
    </v-form>
    <template #actions>
      <dialog-action @click="close">{{ $t('button.cancel') }}</dialog-action>
      <dialog-action
       @click.stop="save"
       :disabled="!place || saving || !valid || !hasUnsavedChanges"
      >
        {{ saving ? $t('button.saving') : $t('button.save') }}
      </dialog-action>
    </template>
  </card-dialog>
</template>

<i18n lang="yaml">
ja:
  label:
    edit_place: 設置場所の編集
    saving: 保存中
  button:
    cancel: キャンセル
    save: 保存
    saving: 保存中...
  field:
    name: 名称
    assignees: 担当者
    select_assignees: 担当者を選択
  messages:
    place_with_name_exists: この名称は既に使用されています
    failed_to_load_users: 組織ユーザーの読み込みに失敗しました
en:
  label:
    edit_place: Edit Place
  button:
    cancel: Cancel
    save: Save
    saving: Saving...
  field:
    name: Name
    assignees: Assignees
    select_assignees: Select assignees
  messages:
    place_with_name_exists: This name is already in use
    failed_to_load_users: Failed to load organization users
</i18n>

<script>
import Features from '@/mixins/features';
import Notifications from '@/mixins/notifications';
import Validations from '@/components/Form/mixins/form-validations';

import CardDialog from '@/components/atoms/CardDialog';
import DialogAction from '@/components/atoms/DialogAction';
import FormField from '@/components/atoms/FormField';
import TextField from '@/components/atoms/TextField';

export default {
  name: 'station-place-editor',
  mixins: [
    Features,
    Notifications,
    Validations,
  ],
  props: {
    value: {
      type: Boolean,
    },
    place: {
      type: Object,
    },
    users: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    CardDialog,
    DialogAction,
    FormField,
    TextField,
  },
  computed: {
    changedAttributes() {
      const result = {};
      if (this.form.name !== this.place?.name) {
        result.name = this.form.name;
      }
      if (this.hasAssigneeChanges) {
        result.assignee_ids = this.form.assignee_ids;
      }
      return result;
    },
    hasAssigneeChanges() {
      return this._.xor(
        this.filterValidAssigneeIds(this.place?.assignee_ids || []),
        this.form.assignee_ids,
      ).length > 0;
    },
    hasUnsavedChanges() {
      return !this.place || Object.keys(this.changedAttributes).length > 0;
    },
    organizationUsers() {
      return this.users;
    },
  },
  data() {
    return {
      form: {
        name: '',
        assignee_ids: [],
      },
      valid: false,
      saving: false,
    };
  },
  inject: ['api', 'getStationPlaces'],
  methods: {
    filterValidAssigneeIds(assigneeIds) {
      const validIds = this.users.map(u => u.id).filter(id => id);
      return assigneeIds.filter(id => validIds.includes(id));
    },
    save: async function () {
      try {
        this.saving = true;
        const updated = await this.api.updateStationPlace(this.place.id, this.changedAttributes);
        this.$emit('update', updated);
        this.close();
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.$emit('input', false);
    },
    noDuplicates() {
      if (this.form.name === this.place?.name) {
        return true;
      }

      const placeWithSameName = this.getStationPlaces().find(
        place => place.name === this.form.name,
      );
      return !placeWithSameName || this.$t('messages.place_with_name_exists');
    },
  },
  watch: {
    place(value) {
      this.form.name = value ? value.name : '';
      this.form.assignee_ids = value ? this.filterValidAssigneeIds(value.assignee_ids) : [];
    },
  },
};
</script>

<style scoped lang="sass">
</style>
